import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, SendIcon, CreditCardIcon, ShieldCheckIcon, ArrowDownLeftIcon, ArrowUpRightIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { formatNaira, formatDate } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface WalletProps {
  navigate: (screen: string) => void;
  viewMode?: 'app' | 'tablet';
}
export function Wallet({
  navigate,
  viewMode = 'app'
}: WalletProps) {
  const [copied, setCopied] = useState(false);
  const walletBalance = 2450000;
  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const transactions = [{
    id: 1,
    type: 'payment',
    title: 'Contribution: Future Scholar Fund',
    date: '2026-03-15',
    amount: -16666,
    icon: ShieldCheckIcon,
    color: 'text-[#1a1a2e] bg-gray-100'
  }, {
    id: 2,
    type: 'fund',
    title: 'Wallet Funding via Paystack',
    date: '2026-03-14',
    amount: 50000,
    icon: ArrowDownLeftIcon,
    color: 'text-emerald-600 bg-emerald-100'
  }, {
    id: 3,
    type: 'claim',
    title: 'Claim Payout: TravelGuard',
    date: '2026-03-10',
    amount: 120000,
    icon: ArrowDownLeftIcon,
    color: 'text-emerald-600 bg-emerald-100'
  }, {
    id: 4,
    type: 'transfer',
    title: 'Transfer to GTBank',
    date: '2026-03-05',
    amount: -25000,
    icon: ArrowUpRightIcon,
    color: 'text-[#1a1a2e] bg-gray-100'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  const renderBalanceCard = () => <>
      <div className="absolute top-0 right-0 w-48 h-48 bg-[#FFD700]/20 rounded-full blur-3xl -mr-12 -mt-12 pointer-events-none" data-id="element-3203" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#2BA89D]/20 rounded-full blur-3xl -ml-8 -mb-8 pointer-events-none" data-id="element-3204" />

      <div className="relative z-10" data-id="element-3205">
        <div className="flex items-center justify-between mb-6" data-id="element-3206">
          <p className="text-white/70 text-xs font-bold uppercase tracking-widest" data-id="element-3207">
            Available Balance
          </p>
          <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/10 text-white/90 px-3 py-1.5 rounded-full text-[10px] font-bold backdrop-blur-sm" data-id="element-3208">
            <ShieldCheckIcon className="w-3.5 h-3.5 text-[#FFD700]" data-id="element-3209" /> Tier 2
          </div>
        </div>

        <motion.h1 initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} className="text-4xl font-black text-white tracking-tight mb-2" data-id="element-3210">
          {formatNaira(walletBalance)}
        </motion.h1>
        <p className="text-white/50 text-xs font-medium" data-id="element-3211">Skydd Wallet • NGN</p>
      </div>

      {/* Account Details */}
      <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between" data-id="element-3212">
        <div data-id="element-3213">
          <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider mb-0.5" data-id="element-3214">
            Wema Bank
          </p>
          <p className="text-white font-mono font-bold tracking-wider" data-id="element-3215">
            0123456789
          </p>
        </div>
        <button onClick={handleCopy} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-id="element-3216">
          {copied ? <CheckIcon className="w-4 h-4 text-emerald-400" data-id="element-3217" /> : <CopyIcon className="w-4 h-4 text-white" data-id="element-3218" />}
        </button>
      </div>
    </>;
  const renderTransactions = () => <>
      {transactions.length > 0 ? transactions.map(tx => <motion.div onClick={() => navigate('transaction-detail', tx.id.toString())} variants={itemVariants} key={tx.id} className={`flex items-center justify-between bg-white p-4 rounded-[1.5rem] border border-[#0D3B3B]/5 shadow-sm hover:shadow-md hover:border-[#2BA89D]/20 transition-all cursor-pointer group ${viewMode === 'tablet' ? 'hover:bg-gray-50/50' : ''}`} data-id="element-3219">
            <div className="flex items-center gap-3" data-id="element-3220">
              <div className={`w-12 h-12 rounded-[1rem] flex items-center justify-center group-hover:scale-110 transition-transform duration-300 ${tx.color}`} data-id="element-3221">
                <tx.icon className="w-5 h-5" data-id="element-3222" />
              </div>
              <div data-id="element-3223">
                <h4 className="font-bold text-[#0D3B3B] text-sm group-hover:text-[#2BA89D] transition-colors" data-id="element-3224">
                  {tx.title}
                </h4>
                <p className="text-[11px] font-medium text-gray-500 mt-0.5" data-id="element-3225">
                  {formatDate(tx.date)}
                </p>
              </div>
            </div>
            <div className={`font-black text-sm ${tx.amount > 0 ? 'text-emerald-600' : 'text-[#0D3B3B]'}`} data-id="element-3226">
              {tx.amount > 0 ? '+' : ''}
              {formatNaira(tx.amount)}
            </div>
          </motion.div>) : <div className="text-center py-8 text-gray-500 text-sm font-medium bg-white rounded-[1.5rem] border border-gray-100" data-id="element-3227">
          No transactions found.
        </div>}
    </>;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className={`flex-1 overflow-y-auto ${viewMode === 'app' ? 'pb-24' : 'pb-6'} bg-slate-50`} data-id="element-3228">
      <TopBar title="Wallet" onNotificationClick={() => navigate('notifications')} onProfileClick={() => navigate('profile')} data-id="element-3229" />

      <div className={`px-5 pt-4 pb-6 ${viewMode === 'tablet' ? 'max-w-6xl mx-auto grid grid-cols-12 gap-8 items-start' : ''}`} data-id="element-3230">
        {/* Left Column (Web) / Top Section (Mobile) */}
        <div className={viewMode === 'tablet' ? 'col-span-5 flex flex-col gap-6' : 'flex flex-col'} data-id="element-3231">
          {/* Balance Section */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className={`relative rounded-[2rem] bg-[#0D3B3B] p-6 overflow-hidden shadow-2xl shadow-[#0D3B3B]/20 ${viewMode === 'app' ? 'mb-8' : ''}`} data-id="element-3232">
            {renderBalanceCard()}
          </motion.div>

          {/* Action Buttons */}
          <div className={`flex justify-between gap-2 ${viewMode === 'app' ? 'mb-12' : 'bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm'}`} data-id="element-3233">
            <button onClick={() => navigate('fund-wallet')} className="flex flex-col items-center gap-2 group flex-1" data-id="element-3234">
              <div className="w-14 h-14 rounded-2xl bg-[#FFD700] text-[#0D3B3B] flex items-center justify-center shadow-lg shadow-[#FFD700]/30 group-active:scale-95 transition-transform" data-id="element-3235">
                <PlusIcon className="w-6 h-6" strokeWidth={2.5} data-id="element-3236" />
              </div>
              <span className="text-[11px] font-bold text-[#0D3B3B]" data-id="element-3237">Fund</span>
            </button>
            <button onClick={() => navigate('transfer')} className="flex flex-col items-center gap-2 group flex-1" data-id="element-3238">
              <div className="w-14 h-14 rounded-2xl bg-gray-50 text-[#0D3B3B] border border-gray-100 flex items-center justify-center group-active:scale-95 transition-all hover:bg-white hover:shadow-md hover:border-[#2BA89D]/20 group-hover:text-[#2BA89D]" data-id="element-3239">
                <SendIcon className="w-5 h-5" data-id="element-3240" />
              </div>
              <span className="text-[11px] font-bold text-[#0D3B3B]" data-id="element-3241">
                Transfer
              </span>
            </button>
            <button onClick={() => navigate('card-settings')} className="flex flex-col items-center gap-2 group flex-1" data-id="element-3242">
              <div className="w-14 h-14 rounded-2xl bg-gray-50 text-[#0D3B3B] border border-gray-100 flex items-center justify-center group-active:scale-95 transition-all hover:bg-white hover:shadow-md hover:border-[#2BA89D]/20 group-hover:text-[#2BA89D]" data-id="element-3243">
                <CreditCardIcon className="w-5 h-5" data-id="element-3244" />
              </div>
              <span className="text-[11px] font-bold text-[#0D3B3B]" data-id="element-3245">
                Cards
              </span>
            </button>
          </div>
        </div>

        {/* Right Column (Web) / Bottom Section (Mobile) */}
        <div className={viewMode === 'tablet' ? 'col-span-7 bg-white p-8 rounded-[2rem] shadow-sm border border-gray-100 h-full' : ''} data-id="element-3246">
          <div className="flex items-center justify-between mb-4 px-1" data-id="element-3247">
            <h3 className="text-lg font-black text-[#0D3B3B] tracking-tight" data-id="element-3248">
              Recent Transactions
            </h3>
            {viewMode === 'tablet' && <button className="text-sm font-bold text-[#2BA89D] hover:text-[#0D3B3B] transition-colors" data-id="element-3249">
                View All
              </button>}
          </div>

          <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-3" data-id="element-3250">
            {renderTransactions()}
          </motion.div>
        </div>
      </div>
    </motion.div>;
}